// SCR-STAFF-851 (AP-INS-01؛ US-INS-010 تا 015): طرح‌های بیمه با سال بیمه‌ای شمسی، قواعد پوشش (درصد از **مبنای بیمه‌گر**، پوشش فرانشیز
// پایه و مابه‌التفاوت تا قیمت مرکز)، سقف‌ها (مبلغی و تعدادی، در دامنه بیمه‌شده یا خانواده) و استثناها (سن، جنسیت، دوره انتظار).
// دقیق‌ترین دامنه قاعده مقدم است: خدمت ← دسته ← همه (AC-INS-011.1)؛ دو قاعده با یک دامنه پذیرفته نمی‌شود، چون نتیجه را غیرقطعی می‌کرد
// (BR-INS-040).
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  unwrap,
  type InsCapKind,
  type InsCapRequest,
  type InsCapScope,
  type InsCoverageRuleRequest,
  type InsExclusionRequest,
  type InsPlanView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  ListPage,
  Money,
  MoneyFieldRHF,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { WriteNotice } from '../settings/common';
import { CAP_KINDS, CAP_SCOPES, insuranceKeys, percentOf, toBasisPoints } from './common';

export const SCR_STAFF_851 = 'SCR-STAFF-851';

export function PlansPage() {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const write = useWriteAccess('ins.plan.manage');
  const [creating, setCreating] = useState(false);
  const [editingRules, setEditingRules] = useState<InsPlanView | null>(null);
  const plans = useQuery({
    queryKey: insuranceKeys.plans(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/plans')),
  });

  const columns: DataTableColumn<InsPlanView>[] = [
    { id: 'name', headerKey: 'ins:plans.columns.name', cell: (row) => row.name },
    { id: 'insurer', headerKey: 'ins:plans.columns.insurer', cell: (row) => row.insurerName },
    {
      id: 'validity',
      headerKey: 'ins:plans.columns.validity',
      cell: (row) => (
        <Stack spacing={0.25}>
          <JalaliDate value={row.validFrom} />
          {row.validTo ? <JalaliDate value={row.validTo} /> : <Typography variant="caption">{t('ins:plans.open_ended')}</Typography>}
        </Stack>
      ),
    },
    {
      id: 'cap_year',
      headerKey: 'ins:plans.columns.cap_year',
      cell: (row) =>
        t('ins:plans.cap_year_start', {
          month: faDigits(Number(row.capYearStartMonth)),
          day: faDigits(Number(row.capYearStartDay)),
        }),
    },
    {
      id: 'rules',
      headerKey: 'ins:plans.columns.rules',
      cell: (row) =>
        t('ins:plans.rule_summary', {
          rules: faDigits(row.rules.length),
          caps: faDigits(row.caps.length),
          exclusions: faDigits(row.exclusions.length),
        }),
    },
    {
      id: 'preauth',
      headerKey: 'ins:plans.columns.preauth',
      cell: (row) => faDigits(row.preAuthServiceIds.length + row.preAuthCategoryIds.length),
      align: 'right',
    },
    {
      id: 'status',
      headerKey: 'ins:plans.columns.status',
      cell: (row) => <StatusChip tone={row.isActive ? 'active' : 'inactive'} />,
    },
    {
      id: 'actions',
      headerKey: 'ins:plans.columns.actions',
      cell: (row) =>
        access.can('ins.plan.manage') ? (
          <Button
            size="small"
            onClick={() => setEditingRules(row)}
            disabled={!write.canWrite}
            data-testid={`ins-plan-rules-${row.planId}`}
          >
            {t('ins:plans.edit_rules')}
          </Button>
        ) : null,
    },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_851}
      titleKey="ins:plans.title"
      subtitleKey="ins:plans.subtitle"
      actions={
        access.can('ins.plan.manage') ? (
          <Button variant="contained" onClick={() => setCreating(true)} disabled={!write.canWrite} data-testid="ins-plan-add">
            {t('ins:plans.add')}
          </Button>
        ) : undefined
      }
      filters={<WriteNotice write={write} />}
    >
      <DataTable
        captionKey="ins:plans.caption"
        columns={columns}
        rows={plans.data}
        getRowId={(row) => row.planId}
        isLoading={plans.isPending}
        error={plans.error}
        onRetry={() => void plans.refetch()}
        emptyTitleKey="ins:plans.empty"
      />
      {creating && <PlanDialog onClose={() => setCreating(false)} />}
      {editingRules && <RulesDialog plan={editingRules} onClose={() => setEditingRules(null)} />}
    </ListPage>
  );
}

const planSchema = z
  .object({
    insurerId: z.string().min(1, 'common:validation.required'),
    name: zRequiredText(200),
    validFrom: z.string().min(1, 'common:validation.required'),
    capYearStartMonth: z.string(),
    capYearStartDay: z.string(),
  })
  .superRefine((form, ctx) => {
    if (!(Number(form.capYearStartMonth) >= 1 && Number(form.capYearStartMonth) <= 12)) {
      ctx.addIssue({ code: 'custom', path: ['capYearStartMonth'], message: 'ins:plans.cap_year_invalid' });
    }

    if (!(Number(form.capYearStartDay) >= 1 && Number(form.capYearStartDay) <= 31)) {
      ctx.addIssue({ code: 'custom', path: ['capYearStartDay'], message: 'ins:plans.cap_year_invalid' });
    }
  });

type PlanForm = z.input<typeof planSchema>;

function PlanDialog({ onClose }: { onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const insurers = useQuery({
    queryKey: insuranceKeys.insurers(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/insurers')),
  });
  const form = useForm<PlanForm>({
    resolver: zodResolver(planSchema),
    defaultValues: { insurerId: '', name: '', validFrom: '', capYearStartMonth: '1', capYearStartDay: '1' },
  });

  const save = useMutation({
    mutationFn: (values: PlanForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/plans', {
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            insurerId: values.insurerId,
            contractId: null,
            name: values.name,
            validFrom: values.validFrom,
            validTo: null,
            capYearStartMonth: Number(values.capYearStartMonth),
            capYearStartDay: Number(values.capYearStartDay),
          },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.plans(session?.tenantId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, form.setError, { fields: ['name', 'capYearStartMonth', 'capYearStartDay'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      titleKey="ins:plans.add_title"
      onClose={onClose}
      onSubmit={form.handleSubmit((values) => save.mutate(values))}
      isDirty={form.formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <SelectRHF
          control={form.control}
          name="insurerId"
          labelKey="ins:plans.fields.insurer"
          options={(insurers.data?.items ?? []).map((insurer) => ({ value: insurer.insurerId, label: insurer.name }))}
          data-testid="ins-plan-insurer"
        />
        <TextFieldRHF control={form.control} name="name" labelKey="ins:plans.fields.name" data-testid="ins-plan-name" />
        <span data-testid="ins-plan-valid-from">
          <JalaliDatePickerRHF control={form.control} name="validFrom" labelKey="ins:plans.fields.valid_from" />
        </span>
        <TextFieldRHF
          control={form.control}
          name="capYearStartMonth"
          labelKey="ins:plans.fields.cap_year_month"
          helperKey="ins:plans.fields.cap_year_hint"
          type="number"
          data-testid="ins-plan-cap-month"
        />
        <TextFieldRHF
          control={form.control}
          name="capYearStartDay"
          labelKey="ins:plans.fields.cap_year_day"
          type="number"
          data-testid="ins-plan-cap-day"
        />
      </Stack>
    </FormDialog>
  );
}

const ruleRowSchema = z
  .object({
    coveragePercent: z.string(),
    coversBaseFranchise: z.boolean(),
    coversDifferenceToGross: z.boolean(),
  })
  .superRefine((form, ctx) => {
    if (!(Number(form.coveragePercent) >= 0 && Number(form.coveragePercent) <= 100)) {
      ctx.addIssue({ code: 'custom', path: ['coveragePercent'], message: 'ins:plans.percent_invalid' });
    }
  });

type RuleRowForm = z.input<typeof ruleRowSchema>;

const capRowSchema = z
  .object({
    code: zRequiredText(40),
    kind: z.string(),
    scope: z.string(),
    amountLimit: z.number().nullable(),
    countLimit: z.string(),
  })
  .superRefine((form, ctx) => {
    // سقف تعدادی تعداد جلسات می‌خواهد و بقیه سقف‌ها مبلغ (AC-INS-012.1).
    if (form.kind === 'SessionCountAnnual') {
      if (!(Number(form.countLimit) >= 1)) {
        ctx.addIssue({ code: 'custom', path: ['countLimit'], message: 'ins:plans.cap_count_invalid' });
      }

      return;
    }

    if (!(form.amountLimit && form.amountLimit > 0)) {
      ctx.addIssue({ code: 'custom', path: ['amountLimit'], message: 'ins:plans.cap_amount_invalid' });
    }
  });

type CapRowForm = z.input<typeof capRowSchema>;

/**
 * قواعد، سقف‌ها و استثناهای طرح در یک عملیات جایگزین می‌شوند (US-INS-011 تا 014): کاربر فهرست را می‌چیند و یک‌جا ذخیره می‌کند، پس
 * نتیجه نیمه‌کاره نمی‌ماند.
 */
function RulesDialog({ plan, onClose }: { plan: InsPlanView; onClose: () => void }) {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const [rules, setRules] = useState<InsCoverageRuleRequest[]>([...plan.rules]);
  const [caps, setCaps] = useState<InsCapRequest[]>([...plan.caps]);
  const [exclusions, setExclusions] = useState<InsExclusionRequest[]>([...plan.exclusions]);
  const [addingRule, setAddingRule] = useState(false);
  const [addingCap, setAddingCap] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>();

  const save = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.PUT('/api/v1/insurance/plans/{id}/rules', {
          params: { path: { id: plan.planId } },
          body: {
            rules,
            caps,
            exclusions,
            preAuthServiceIds: plan.preAuthServiceIds,
            preAuthCategoryIds: plan.preAuthCategoryIds,
          },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.plans(session?.tenantId) });
      onClose();
    },
    onError: (error) => setSubmitError(error),
  });

  return (
    <FormDialog
      open
      titleKey="ins:plans.rules_title"
      onClose={onClose}
      onSubmit={() => save.mutate()}
      isDirty
      isSubmitting={save.isPending}
      submitError={submitError}
      maxWidth="md"
    >
      <Stack spacing={2}>
        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">{t('ins:plans.rules_section')}</Typography>
            <Button size="small" onClick={() => setAddingRule(true)} data-testid="ins-rule-add">
              {t('ins:plans.add_rule')}
            </Button>
          </Stack>
          <Typography variant="caption">{t('ins:plans.rules_hint')}</Typography>
          {rules.length === 0 ? (
            <Typography variant="body2" data-testid="ins-rules-empty">
              {t('ins:plans.rules_empty')}
            </Typography>
          ) : (
            rules.map((rule, index) => (
              <Stack key={`rule-${index}`} direction="row" alignItems="center" spacing={1} data-testid={`ins-rule-${index}`}>
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {t('ins:plans.rule_row', {
                    percent: percentOf(rule.coverageBasisPoints),
                    scope: rule.serviceId
                      ? t('ins:plans.scope_service')
                      : rule.categoryId
                        ? t('ins:plans.scope_category')
                        : t('ins:plans.scope_all'),
                  })}
                  {rule.coversBaseFranchise ? ` · ${t('ins:plans.covers_franchise')}` : ''}
                  {rule.coversDifferenceToGross ? ` · ${t('ins:plans.covers_difference')}` : ''}
                </Typography>
                <IconButton
                  size="small"
                  aria-label={t('ins:plans.remove')}
                  onClick={() => setRules(rules.filter((_, position) => position !== index))}
                  data-testid={`ins-rule-remove-${index}`}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
            ))
          )}
        </Stack>

        <Stack spacing={1}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle2">{t('ins:plans.caps_section')}</Typography>
            <Button size="small" onClick={() => setAddingCap(true)} data-testid="ins-cap-add">
              {t('ins:plans.add_cap')}
            </Button>
          </Stack>
          <Typography variant="caption">{t('ins:plans.caps_hint')}</Typography>
          {caps.map((cap, index) => (
            <Stack key={cap.code} direction="row" alignItems="center" spacing={1} data-testid={`ins-cap-${cap.code}`}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {cap.code} · {t(`ins:cap_kind.${cap.kind}`)} · {t(`ins:cap_scope.${cap.scope}`)} ·{' '}
                {cap.amountLimit ? <Money amountMinor={Number(cap.amountLimit ?? 0)} /> : faDigits(Number(cap.countLimit ?? 0))}
              </Typography>
              <IconButton
                size="small"
                aria-label={t('ins:plans.remove')}
                onClick={() => setCaps(caps.filter((_, position) => position !== index))}
                data-testid={`ins-cap-remove-${cap.code}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>

        <Stack spacing={1}>
          <Typography variant="subtitle2">{t('ins:plans.exclusions_section')}</Typography>
          <Typography variant="caption">{t('ins:plans.exclusions_hint')}</Typography>
          {exclusions.map((exclusion, index) => (
            <Stack key={`exclusion-${index}`} direction="row" alignItems="center" spacing={1} data-testid={`ins-exclusion-${index}`}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {exclusion.minAgeYears || exclusion.maxAgeYears
                  ? t('ins:plans.exclusion_age', {
                      min: faDigits(Number(exclusion.minAgeYears ?? 0)),
                      max: faDigits(Number(exclusion.maxAgeYears ?? 150)),
                    })
                  : exclusion.waitingPeriodDays
                    ? t('ins:plans.exclusion_waiting', { days: faDigits(Number(exclusion.waitingPeriodDays)) })
                    : t('ins:plans.exclusion_absolute')}
              </Typography>
              <IconButton
                size="small"
                aria-label={t('ins:plans.remove')}
                onClick={() => setExclusions(exclusions.filter((_, position) => position !== index))}
                data-testid={`ins-exclusion-remove-${index}`}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Stack>
      {addingRule && (
        <RuleDialog
          onClose={() => setAddingRule(false)}
          onAdd={(rule) => {
            setRules([...rules, rule]);
            setAddingRule(false);
          }}
        />
      )}
      {addingCap && (
        <CapDialog
          onClose={() => setAddingCap(false)}
          onAdd={(cap) => {
            setCaps([...caps, cap]);
            setAddingCap(false);
          }}
        />
      )}
    </FormDialog>
  );
}

function RuleDialog({ onAdd, onClose }: { onAdd: (rule: InsCoverageRuleRequest) => void; onClose: () => void }) {
  const { t } = useTranslation('ins');
  const form = useForm<RuleRowForm>({
    resolver: zodResolver(ruleRowSchema),
    defaultValues: { coveragePercent: '70', coversBaseFranchise: false, coversDifferenceToGross: false },
  });

  return (
    <FormDialog
      open
      titleKey="ins:plans.add_rule"
      onClose={onClose}
      onSubmit={form.handleSubmit((values) =>
        onAdd({
          serviceId: null,
          categoryId: null,
          coverageBasisPoints: toBasisPoints(Number(values.coveragePercent)),
          coversBaseFranchise: values.coversBaseFranchise,
          coversDifferenceToGross: values.coversDifferenceToGross,
          differenceCeiling: null,
        }),
      )}
      isDirty={form.formState.isDirty}
      isSubmitting={false}
    >
      <Stack spacing={2}>
        <TextFieldRHF
          control={form.control}
          name="coveragePercent"
          labelKey="ins:plans.fields.coverage_percent"
          helperKey="ins:plans.fields.coverage_percent_hint"
          type="number"
          data-testid="ins-rule-percent"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.watch('coversBaseFranchise')}
              onChange={(event) => form.setValue('coversBaseFranchise', event.target.checked, { shouldDirty: true })}
              data-testid="ins-rule-franchise"
            />
          }
          label={t('ins:plans.fields.covers_franchise')}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.watch('coversDifferenceToGross')}
              onChange={(event) => form.setValue('coversDifferenceToGross', event.target.checked, { shouldDirty: true })}
              data-testid="ins-rule-difference"
            />
          }
          label={t('ins:plans.fields.covers_difference')}
        />
      </Stack>
    </FormDialog>
  );
}

function CapDialog({ onAdd, onClose }: { onAdd: (cap: InsCapRequest) => void; onClose: () => void }) {
  const form = useForm<CapRowForm>({
    resolver: zodResolver(capRowSchema),
    defaultValues: { code: '', kind: 'TotalAnnual', scope: 'Insured', amountLimit: null, countLimit: '0' },
  });

  return (
    <FormDialog
      open
      titleKey="ins:plans.add_cap"
      onClose={onClose}
      onSubmit={form.handleSubmit((values) =>
        onAdd({
          code: values.code,
          kind: values.kind as InsCapKind,
          scope: values.scope as InsCapScope,
          serviceId: null,
          categoryId: null,
          amountLimit: values.kind === 'SessionCountAnnual' ? null : (values.amountLimit ?? 0),
          countLimit: values.kind === 'SessionCountAnnual' ? Number(values.countLimit) : null,
        }),
      )}
      isDirty={form.formState.isDirty}
      isSubmitting={false}
    >
      <Stack spacing={2}>
        <TextFieldRHF
          control={form.control}
          name="code"
          labelKey="ins:plans.fields.cap_code"
          helperKey="ins:plans.fields.cap_code_hint"
          data-testid="ins-cap-code"
        />
        <SelectRHF
          control={form.control}
          name="kind"
          labelKey="ins:plans.fields.cap_kind"
          options={CAP_KINDS.map((kind) => ({ value: kind, labelKey: `ins:cap_kind.${kind}` }))}
          data-testid="ins-cap-kind"
        />
        <SelectRHF
          control={form.control}
          name="scope"
          labelKey="ins:plans.fields.cap_scope"
          helperKey="ins:plans.fields.cap_scope_hint"
          options={CAP_SCOPES.map((scope) => ({ value: scope, labelKey: `ins:cap_scope.${scope}` }))}
          data-testid="ins-cap-scope"
        />
        <span data-testid="ins-cap-amount">
          <MoneyFieldRHF control={form.control} name="amountLimit" labelKey="ins:plans.fields.cap_amount" fullWidth />
        </span>
        <TextFieldRHF
          control={form.control}
          name="countLimit"
          labelKey="ins:plans.fields.cap_count"
          type="number"
          data-testid="ins-cap-count"
        />
      </Stack>
    </FormDialog>
  );
}
